import { GameState, Piece, PlayerColor } from './types';
import { isSafeStep, nextPlayer, stepsToCoord, trackIndex, TRACK_OFFSET } from './board';

// ── Начальное состояние ───────────────────────────────────────────────────
export function createInitialState(): GameState {
  const pieces: Piece[] = [];
  const colors: PlayerColor[] = ['red', 'green', 'yellow', 'blue'];
  for (const player of colors) {
    for (let id = 0; id < 4; id++) {
      pieces.push({ id, player, steps: -1 }); // все начинают дома
    }
  }
  return {
    pieces,
    currentPlayer: 'red',
    phase: 'idle',
    winner: null,
    dice: { value: 0, rolling: false, used: false },
    anim: null,
    selectablePieces: [],
    extraTurn: false,
  };
}

// ── Фишки одного игрока ───────────────────────────────────────────────────
export function playerPieces(state: GameState, player: PlayerColor): Piece[] {
  return state.pieces.filter(p => p.player === player);
}

// ── Доступные ходы ────────────────────────────────────────────────────────
export function validMoves(state: GameState): number[] {
  const dice = state.dice.value;
  const player = state.currentPlayer;
  const pieces = playerPieces(state, player);
  const selectable: number[] = [];

  for (const piece of pieces) {
    if (piece.steps === 57) continue; // уже финишировала

    if (piece.steps === -1) {
      // Выход из дома — только при 6, и клетка steps=0 не занята своей фишкой
      if (dice === 6 && !wouldLandOnOwn(state, player, piece, 0)) selectable.push(piece.id);
      continue;
    }

    const toSteps = piece.steps + dice;
    if (toSteps > 57) continue; // overshoot финиша

    // Нельзя встать на клетку, где уже стоит своя фишка
    if (toSteps < 57 && wouldLandOnOwn(state, player, piece, toSteps)) continue;

    selectable.push(piece.id);
  }

  return selectable;
}

// Стоит ли своя фишка на целевой клетке
function wouldLandOnOwn(
  state: GameState,
  player: PlayerColor,
  moving: Piece,
  toSteps: number
): boolean {
  const [tr, tc] = stepsToCoord(player, toSteps);
  return state.pieces.some(p =>
    p.player === player &&
    p.id !== moving.id &&
    p.steps >= 0 &&
    p.steps !== 57
    && (() => { const [r, c] = stepsToCoord(player, p.steps); return r === tr && c === tc; })()
  );
}

// ── Применить ход ─────────────────────────────────────────────────────────
export function applyMove(state: GameState, pieceId: number, nowMs: number): GameState {
  const player = state.currentPlayer;
  const dice   = state.dice.value;

  const pieceIdx = state.pieces.findIndex(p => p.player === player && p.id === pieceId);
  if (pieceIdx === -1) return state;

  const piece = state.pieces[pieceIdx];
  const fromSteps = piece.steps;
  const toSteps   = fromSteps === -1 ? 0 : fromSteps + dice;

  // Строим путь для анимации (каждый шаг отдельно)
  const path: number[] = [];
  if (fromSteps === -1) {
    path.push(-1, 0);
  } else {
    for (let s = fromSteps + 1; s <= toSteps; s++) path.push(s);
  }

  // Захват: ищем чужую фишку на целевой клетке
  const [toRow, toCol] = stepsToCoord(player, toSteps);
  const capturedIdx = state.pieces.findIndex(p => {
    if (p.player === player) return false;
    if (p.steps < 0 || p.steps === 57) return false;
    if (toSteps >= 51) return false; // в домашней колонке захвата нет
    if (isSafeStep(player, toSteps)) return false; // безопасная клетка
    const [r, c] = stepsToCoord(p.player, p.steps);
    return r === toRow && c === toCol;
  });

  // Обновляем массив фишек
  const newPieces = state.pieces.map((p, i) => {
    if (i === pieceIdx)   return { ...p, steps: toSteps };
    if (i === capturedIdx) return { ...p, steps: -1 };
    return p;
  });

  // Победа?
  const allHome = newPieces.filter(p => p.player === player).every(p => p.steps === 57);
  const extraTurn = dice === 6 && !allHome;

  return {
    ...state,
    pieces: newPieces,
    phase: 'moving',
    winner: allHome ? player : null,
    dice: { ...state.dice, used: true },
    selectablePieces: [],
    extraTurn,
    anim: {
      player,
      pieceId,
      path,
      stepIdx: 0,
      startMs: nowMs,
      msPerStep: 80,
    },
  };
}

// ── Следующий ход ─────────────────────────────────────────────────────────
export function nextTurn(state: GameState): GameState {
  const next = state.extraTurn ? state.currentPlayer : nextPlayer(state.currentPlayer);
  return {
    ...state,
    currentPlayer: next,
    phase: 'idle',
    dice: { value: 0, rolling: false, used: false },
    selectablePieces: [],
    extraTurn: false,
    anim: null,
  };
}

// ── Бросок кубика ─────────────────────────────────────────────────────────
export function rollDice(state: GameState): GameState {
  const value = Math.floor(Math.random() * 6) + 1;
  return {
    ...state,
    phase: 'rolling',
    dice: { value, rolling: true, used: false },
  };
}

export function finishRoll(state: GameState): GameState {
  const moves = validMoves({ ...state, dice: { ...state.dice, rolling: false } });
  const hasMoves = moves.length > 0;
  return {
    ...state,
    phase: hasMoves ? 'selecting' : 'idle',
    dice: { ...state.dice, rolling: false },
    selectablePieces: moves,
  };
}

// ── Проверка: является ли ход опасным (AI-подсказка) ─────────────────────
// Стоит ли противник так, что может захватить нас на toSteps
export function isInDanger(
  state: GameState,
  player: PlayerColor,
  toSteps: number
): boolean {
  if (toSteps >= 51 || isSafeStep(player, toSteps)) return false;
  const [tr, tc] = stepsToCoord(player, toSteps);
  return state.pieces.some(p => {
    if (p.player === player || p.steps < 0 || p.steps === 57) return false;
    // Может ли враг достичь нашей целевой клетки за 1-6 ходов?
    for (let d = 1; d <= 6; d++) {
      const es = p.steps + d;
      if (es >= 51) continue;
      const [er, ec] = stepsToCoord(p.player, es);
      if (er === tr && ec === tc) return true;
    }
    return false;
  });
}

// ── Может ли ход захватить противника ─────────────────────────────────────
export function wouldCapture(
  state: GameState,
  player: PlayerColor,
  toSteps: number
): boolean {
  if (toSteps >= 51 || isSafeStep(player, toSteps)) return false;
  const [tr, tc] = stepsToCoord(player, toSteps);
  return state.pieces.some(p => {
    if (p.player === player || p.steps < 0 || p.steps === 57) return false;
    const [r, c] = stepsToCoord(p.player, p.steps);
    return r === tr && c === tc;
  });
}

// ── Шаг анимации (чисто визуальный) ──────────────────────────────────────
// Возвращает { state, done } — done=true когда анимация закончилась.
// Переходы между ходами обрабатываются в main.ts.
export function tickAnim(state: GameState, nowMs: number): { state: GameState; done: boolean } {
  if (!state.anim) return { state, done: false };
  const { anim } = state;
  const elapsed = nowMs - anim.startMs;
  const stepIdx = Math.floor(elapsed / anim.msPerStep);

  if (stepIdx >= anim.path.length) {
    return { state: { ...state, anim: null }, done: true };
  }
  return { state: { ...state, anim: { ...anim, stepIdx } }, done: false };
}

// ── Текущая экранная позиция анимируемой фишки ────────────────────────────
export function animPieceSteps(state: GameState): number {
  if (!state.anim) return -2;
  const { path, stepIdx } = state.anim;
  return path[Math.min(stepIdx, path.length - 1)];
}

// ── Глобальный индекс трека ───────────────────────────────────────────────
export { trackIndex, TRACK_OFFSET };
